/**
 * THE OPEN DIALOGS, IN THE ORDER THEY OPENED. Every dialog on the shared
 * keyboard contract (hooks/useDialogFocus, and through it usePromptDialog and
 * the ConfirmModal) puts one layer here while it is open and takes it out when
 * it closes. Two readers:
 *
 *  - useDialogFocus answers Tab and Escape only for the TOP layer, so one
 *    Escape closes one dialog. Two nested confirms used to both cancel on one
 *    key: each listened on the window in the capture phase, and
 *    `stopPropagation` does not stop a second listener on the same target
 *    (#1159 review round 1).
 *  - Android's hardware Back (useNativeBackButton) asks `closeTopModalLayer()`
 *    first: Back is Android's Escape, so it answers the dialog on top instead
 *    of navigating the page behind it (or exiting the app on /app).
 *
 * A layer that is not LIVE is skipped, not removed: a sheet stepping aside
 * for a gate (`yieldToOtherModals`), a trap whose container is not in the
 * document yet, or a dialog that is in the document but not RENDERED (inside
 * a `display: none` subtree: Preferences left open on the 1:1 tab when a
 * call starts, while the layout hides <main> and the call stage is portalled
 * out; #1161 review). The next live layer below it is the top. "Open" is
 * not "on screen": a layer is mounted from open to close, whatever hides it.
 */
export interface ModalLayer {
  /**
   * Android Back over this layer: the dialog's own dismiss (its Escape,
   * unless it asked for something else). A dialog that must be answered
   * passes a no-op, so Back is swallowed there and the page behind stays.
   */
  back: () => void;
  /** Is this layer rendered, in the document, and answering keys right now? */
  live: () => boolean;
}

/**
 * Is `el` rendered (not inside a `display: none` subtree)? The browser's own
 * answer where it has one (`checkVisibility()`, Chrome 105 / Safari 17.4);
 * on an older WebView, the ancestors' computed `display`. Not
 * `offsetParent` (null for every `position: fixed` element, which most
 * dialogs are) nor `getClientRects()` (empty in jsdom for everything).
 */
export function isRendered(el: Element): boolean {
  const check = (el as Element & { checkVisibility?: () => boolean }).checkVisibility;
  if (typeof check === 'function') return check.call(el);
  const view = el.ownerDocument.defaultView;
  if (!view) return true;
  for (let node: Element | null = el; node; node = node.parentElement) {
    if (view.getComputedStyle(node).display === 'none') return false;
  }
  return true;
}

const layers: ModalLayer[] = [];

/** Put a layer on top. Returns its removal (idempotent). */
export function openModalLayer(layer: ModalLayer): () => void {
  layers.push(layer);
  return () => {
    const at = layers.lastIndexOf(layer);
    if (at >= 0) layers.splice(at, 1);
  };
}

/** The live layer that opened last, or null with no dialog up. */
export function topModalLayer(): ModalLayer | null {
  for (let i = layers.length - 1; i >= 0; i -= 1) {
    if (layers[i].live()) return layers[i];
  }
  return null;
}

/**
 * Android Back: answer the top dialog. True when a dialog took the press
 * (closed, or swallowed it because it must be answered); false with none up,
 * so Back goes on to its other meanings.
 */
export function closeTopModalLayer(): boolean {
  const top = topModalLayer();
  if (!top) return false;
  top.back();
  return true;
}
